import React, { useState, useCallback, useEffect } from 'react'
import { Formik, Form, Field } from 'formik'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LayoutWithAccountCard from '../../../../layout/Layout/layout-account-card'
import NumberFormat from 'react-number-format'
import {
  BENEFICIARY_TRANSFER_KEYS,
  DEPOSITS_KEYS,
  SIDENAV_KEYS,
  ADD_BENEFICIARY,
} from '../../../../utils/translation-constants'
import { NeoOTP, NeoSuccess } from '../../../../components/FormComponents'
import NeoButton from '../../../../components/NeoButton/NeoButton'
import NeoInput from '../../../../components/NeoInput/NeoInput'
import InputError from '../../../../components/InputError/InputError'
import Loading from 'custom/assets/loaders/Loading'
import { AddInternalBeneficiarySchema } from '../../../../schemas/beneficiary-transfer'
import BeneficiaryTransferService from '../../../../services/beneficiary-transfers.service'
import TermsAndCondition from '../../../../components/TermsAndConditions/TermsAndCondition'
import config from 'custom/config/environment'
import validationConfig from 'custom/config/validation.config.json'
const internalBeneConfig = validationConfig['addBeneficiary']['internal']

export default function AddInternalBeneficiary() {
  const history = useHistory()
  const [stage, setStage] = useState(0)
  const [otpEnabled, setOtpEnabled] = useState(false)
  const [formValues, setFormValues] = useState({
    nickName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    transferAmount: undefined,
    maxTransferAmount: '',
  })
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [otp, setOtp] = useState('')
  const { t } = useTranslation()
  const [verifiedBeneDetails, setVerifiedBeneDetails] = useState({})

  function renderSwitch() {
    switch (stage) {
      case 1:
        return renderForm()
      case 2:
        return renderConfirmation()
      case 3:
        return <NeoOTP otp={otp} setOTP={setOtp} onSubmit={onConfirm} loading={loading} />
      case 4:
        return <NeoSuccess message={message} linkPath='/beneficiary-transfers' />
      default:
        return renderForm()
    }
  }

  const onVerify = useCallback(async (data) => {
    try {
      if (data.transferAmount < config.MIN_TRANSFER_LIMIT) {
        return
      }
      setFormValues(data)
      setLoading(true)
      const response = await BeneficiaryTransferService.verifyAddBeneficiary({
        active: 'Y',
        srcAccount: data.accountNumber,
        beneName: data.nickName,
        maxTransferLimit: data.maxTransferAmount,
        shortName: data.nickName,
        module: 'Beneficary',
        favorites: 'N',
      })
      if (response.status === 200) {
        setOtpEnabled(response.data['otpEnable'])
        setVerifiedBeneDetails(response.data)
        setStage(2)
        setLoading(false)
      }
    } catch (err) {
      setLoading(false)
    }
  }, [])
  const onConfirm = useCallback(async () => {
    try {
      setLoading(true)
      const response = await BeneficiaryTransferService.confirmAddBeneficiary({
        srcAccount: formValues.accountNumber,
        beneBrn: verifiedBeneDetails.beneBrn,
        beneCurrency: verifiedBeneDetails.beneCurrency,
        beneName: verifiedBeneDetails.beneName,
        beneType: 'IAT',
        maxTransferLimit: formValues.maxTransferAmount,
        shortName: formValues.nickName,
        favorites: 'N',
        beneMobileNo: '',
        beneEMail: '',
        module: 'Beneficary',
        otp: otp || '0000',
      })
      if (response.status === 200) {
        setMessage(response.data['message'])
        setLoading(false)
        setStage(4)
      }
    } catch (err) {
      setLoading(false)
      setOtp('')
    }
  }, [formValues, verifiedBeneDetails, otp])

  function renderForm() {
    return (
      <div>
        <div className='heading-style'>
          {t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.ADDINTERNAL)}
        </div>
        <Formik
          initialValues={formValues}
          enableReinitialize
          validationSchema={AddInternalBeneficiarySchema}
          onSubmit={onVerify}>
          {({ isSubmitting, errors, touched, handleChange, values, setFieldValue }) => (
            <Form>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex={'1'}
                    showAsterisk={internalBeneConfig['isNickNameRequired']}
                    value={values.nickName}
                    text={t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.NICK_NAME)}
                    name='nickName'
                    onChange={handleChange}
                    minLength={2}
                    maxLength={20}
                    onKeyPress={(event) => {
                      if (
                        !(event.charCode >= 65 && event.charCode <= 90) &&
                        !(event.charCode >= 97 && event.charCode <= 122) &&
                        event.charCode !== 32
                      ) {
                        event.preventDefault()
                      }
                    }}
                  />
                  <InputError
                    text={errors.nickName}
                    onChange={handleChange}
                    touched={touched.nickName}
                  />
                </div>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex={'2'}
                    showAsterisk={internalBeneConfig['isAccountNoRequired']}
                    text={t(
                      BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.INTERNAL_ACCOUNT_NO,
                    )}
                    // type='number'
                    // mode='onlyNumber'
                    mode='password'
                    onChange={(value) => {
                      setFieldValue('accountNumber', value?.value)
                    }}
                    maxLength={config.ACC_NO_LENGTH}
                    name='accountNumber'
                  />
                  <InputError text={errors.accountNumber} touched={touched.accountNumber} />
                </div>
              </div>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex={'3'}
                    showAsterisk={internalBeneConfig['isConfirmAccountNoRequired']}
                    text={t(
                      BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS
                        .INTERNAL_CONFIRM_ACCOUNT_NO,
                    )}
                    mode='onlyNumber'
                    onChange={(value) => {
                      setFieldValue('confirmAccountNumber', value?.value)
                    }}
                    name='confirmAccountNumber'
                    // type='number'
                    maxLength={config.ACC_NO_LENGTH}
                  />
                  <InputError
                    text={errors.confirmAccountNumber}
                    touched={touched.confirmAccountNumber}
                  />
                </div>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex={'4'}
                    showAsterisk={internalBeneConfig['isTransferLimitRequired']}
                    text={t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.TRANSFER_LIMIT)}
                    type='number'
                    mode='amount'
                    value={values.maxTransferAmount}
                    // onChange={(values) => {
                    //   setFieldValue('maxTransferAmount', values.value)
                    // }}
                    onChange={(value) => {
                      setFieldValue('transferAmount', value.floatValue)
                      setFieldValue('maxTransferAmount', value?.value)
                    }}
                    name='transferAmount'
                  />
                  <InputError text={errors.maxTransferAmount} touched={touched.maxTransferAmount} />
                  <InputError
                    text={`${t(BENEFICIARY_TRANSFER_KEYS.TRANSFER_LIMIT_TXT)} ${
                      config.MIN_TRANSFER_LIMIT
                    }.00`}
                    touched={values.transferAmount < config.MIN_TRANSFER_LIMIT}
                  />
                </div>
              </div>
              <div className='mt-3'>
                {loading ? (
                  renderLoading()
                ) : (
                  <NeoButton
                    tabIndex={'5'}
                    type='submit'
                    text={t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.SUBMIT)}
                    disabled={isSubmitting}
                    mode='newDesign'
                  />
                )}
              </div>
              <div className='back-cancel-btn-container'>
                <button
                  className='back-cancel-btn btn btn-sm new-design-warning-text-color'
                  onClick={() => history.goBack()}
                  tabIndex={'6'}>
                  {' '}
                  {t(SIDENAV_KEYS.TOUCH_ID_KEYS.CANCEL)}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  function renderConfirmation() {
    return (
      <div className='d-flex flex-column confirmation'>
        <h3>{t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.CONFIRMINTERNAL)}</h3>
        <div>
          <h6 className='label'>
            {t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.NICK_NAME1)}
          </h6>
          <h6 className='value'>{formValues.nickName}</h6>
        </div>

        <div>
          <h6 className='label'>{t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.NAME1)}</h6>
          <h6 className='value'>{verifiedBeneDetails.beneName}</h6>
        </div>

        <div>
          <h6 className='label'>
            {t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.INTERNAL_ACCOUNT_NO)}
          </h6>
          <h6 className='value'>{formValues.accountNumber}</h6>
        </div>

        <div>
          <h6 className='label'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_DETAILS_KEYS.brnCode)}</h6>
          <h6 className='value'>{verifiedBeneDetails.beneBrn}</h6>
        </div>

        <div>
          <h6 className='label'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_DETAILS_KEYS.BEN_CUR)}</h6>
          <h6 className='value'>{verifiedBeneDetails.beneCurrency}</h6>
        </div>

        <div>
          <h6 className='label'>
            {t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.TRANSFER_LIMIT1)}
          </h6>
          <h6 className='value'>
            <NumberFormat
              value={formValues.maxTransferAmount}
              displayType={'text'}
              thousandSeparator={true}
              prefix={''}
              fixedDecimalScale={true}
              decimalScale={2}
              allowEmptyFormatting
            />
          </h6>
        </div>

        <div>
          <TermsAndCondition module='BENEFICIARY' />
        </div>

        <div className='mt-3'>
          {loading ? (
            renderLoading()
          ) : (
            <NeoButton
              type='button'
              mode='newDesign'
              className='btn btn-neo'
              onClick={() => (otpEnabled ? setStage(3) : onConfirm())}
              text={t(ADD_BENEFICIARY.CONFIRM)}
            />
          )}
        </div>
        <div className='back-cancel-style' onClick={() => setStage(1)}>
          {t(SIDENAV_KEYS.TOUCH_ID_KEYS.BACK)}
        </div>
      </div>
    )
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  return (
    <LayoutWithAccountCard breadCumKey='BENE_INTERNAL'>
      <div className='new-design-form-style'>{renderSwitch()}</div>
    </LayoutWithAccountCard>
  )
}
